import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './MultiResponseSelector.css';

function shortModel(model) {
  if (!model) return null;
  const slash = model.lastIndexOf('/');
  return slash >= 0 ? model.slice(slash + 1) : model;
}

// Per-translation costs are fractions of a cent; show enough decimals to be meaningful.
function formatCost(cost) {
  if (typeof cost !== 'number') return null;
  return `$${cost.toFixed(6)}`;
}

const isFailed = (r) => typeof r !== 'string' && r != null && r.failed === true;

function MultiResponseSelector({ responses, title, children }) {
  const { t } = useTranslation();
  // Default to the first model that actually succeeded, so a failed primary never lands selected.
  const firstOk = Array.isArray(responses) ? responses.findIndex((r) => !isFailed(r)) : -1;
  const [index, setIndex] = useState(firstOk >= 0 ? firstOk : 0);
  const groupName = useId();

  if (!responses || responses.length <= 1) {
    return null;
  }

  const label = title || t('multiResponse.title');
  // Tolerate the legacy plain-string shape as well as { model, cost, translation }.
  const asText = (r) => (typeof r === 'string' ? r : r.translation);
  const selected = responses[index];
  const selectedFailed = isFailed(selected);
  const selectedModel = typeof selected === 'string' ? null : selected.model;
  const selectedCost = typeof selected === 'string' ? null : formatCost(selected.cost);

  return (
    <div className="multi-response-card">
      <div className="multi-response-header">
        <span className="multi-response-title">{label}</span>
        <div className="multi-response-options" role="radiogroup" aria-label={label}>
          {responses.map((r, i) => {
            const model = typeof r === 'string' ? null : r.model;
            const failed = isFailed(r);
            return (
              <label
                key={i}
                className={`multi-response-option${failed ? ' multi-response-option-failed' : ''}`}
                title={model || undefined}
              >
                <input
                  type="radio"
                  name={groupName}
                  value={i}
                  checked={index === i}
                  disabled={failed}
                  onChange={() => setIndex(i)}
                />
                {shortModel(model) || t('multiResponse.option', { number: i + 1 })}
                {failed && <span className="multi-response-failed-tag"> ({t('multiResponse.failed')})</span>}
              </label>
            );
          })}
        </div>
      </div>
      <div className="multi-response-body">
        {selectedFailed
          ? t('multiResponse.failedBody')
          : typeof children === 'function'
            ? children(selected)
            : asText(selected)}
      </div>
      {(selectedModel || selectedCost) && (
        <div className="multi-response-meta">
          {selectedModel && <span className="multi-response-meta-model">{selectedModel}</span>}
          {!selectedFailed && selectedCost && (
            <span className="multi-response-meta-cost">{selectedCost}</span>
          )}
        </div>
      )}
    </div>
  );
}

export default MultiResponseSelector;
